import { JOURNEY_PHASES, type JourneyPhase } from '@/types/brand'
import type { ArticleDoc } from '@/types/content'
import {
  PAGE_STATUSES,
  type Page,
  type PageKeyword,
  type PageStatus,
  type Topic,
  pageStatus,
} from '@/types/plan'

/** The brand's articles, as far as a page's status reads them. */
export type PageArticles = readonly Pick<ArticleDoc, 'pageId' | 'publishedUrl' | 'verifiedAt'>[]

/** The topics in their plan order. */
export function topicsInOrder(topics: readonly Topic[]): Topic[] {
  return [...topics].sort((a, b) => a.position - b.position)
}

/** The pages of a topic in writing order. */
export function pagesOfTopic(pages: readonly Page[], topicId: string): Page[] {
  return pages
    .filter((page) => page.topicId === topicId)
    .sort((a, b) => a.writingOrder - b.writingOrder)
}

/** The hub page of a topic, or `null` while its pages are not loaded. */
export function hubOf(topic: Pick<Topic, 'hubPageId'>, pages: readonly Page[]): Page | null {
  return pages.find((page) => page.id === topic.hubPageId) ?? null
}

/** The status of a page from the article written for it, if any; live only once its published URL is verified. */
export function statusOf(page: Pick<Page, 'id' | 'existing'>, articles: PageArticles): PageStatus {
  const article = articles.find((entry) => entry.pageId === page.id)
  const isLive = article?.publishedUrl !== undefined && article.verifiedAt !== undefined
  return pageStatus(page, article !== undefined, isLive)
}

/** How many pages are in each status. */
export function statusCounts(
  pages: readonly Page[],
  articles: PageArticles,
): Record<PageStatus, number> {
  const counts = Object.fromEntries(PAGE_STATUSES.map((status) => [status, 0])) as Record<
    PageStatus,
    number
  >
  for (const page of pages) counts[statusOf(page, articles)] += 1
  return counts
}

/** How many pages serve each journey stage. */
export function stageCounts(pages: readonly Page[]): Record<JourneyPhase, number> {
  const counts = Object.fromEntries(JOURNEY_PHASES.map((phase) => [phase, 0])) as Record<
    JourneyPhase,
    number
  >
  for (const page of pages) counts[page.stage] += 1
  return counts
}

/** The keywords of one page, the main one first. */
export function keywordsOfPage(keywords: readonly PageKeyword[], pageId: string): PageKeyword[] {
  return keywords
    .filter((keyword) => keyword.pageId === pageId)
    .sort((a, b) => Number(b.role === 'main') - Number(a.role === 'main'))
}

/** The main keyword of a page, or `null` for a page the plan added without search data. */
export function mainKeywordOf(
  keywords: readonly PageKeyword[],
  pageId: string,
): PageKeyword | null {
  return keywords.find((keyword) => keyword.pageId === pageId && keyword.role === 'main') ?? null
}

/** The monthly searches of a page: the sum over its keywords, unmeasured ones counting 0. */
export function pageVolume(keywords: readonly PageKeyword[], pageId: string): number {
  return keywords
    .filter((keyword) => keyword.pageId === pageId)
    .reduce((sum, keyword) => sum + (keyword.metrics.searchVolume ?? 0), 0)
}

/** The monthly searches of a topic: the sum over its pages. */
export function topicVolume(keywords: readonly PageKeyword[], pages: readonly Page[]): number {
  return pages.reduce((sum, page) => sum + pageVolume(keywords, page.id), 0)
}

/** The first page of a topic still to write, in writing order, that no open run is writing; `null` when none. */
export function nextToWrite(
  pages: readonly Page[],
  articles: PageArticles,
  busyPageIds: ReadonlySet<string>,
): Page | null {
  return (
    pages.find((page) => statusOf(page, articles) === 'planned' && !busyPageIds.has(page.id)) ??
    null
  )
}

/** The pages that argue a belief, in writing order. */
export function pagesArguing(pages: readonly Page[], beliefSlug: string): Page[] {
  return pages
    .filter((page) => page.objective?.beliefSlug === beliefSlug)
    .sort((a, b) => a.writingOrder - b.writingOrder)
}

/** The topic a belief is argued under, or `null`. */
export function topicArguing(topics: readonly Topic[], beliefSlug: string): Topic | null {
  return topics.find((topic) => topic.beliefSlugs.includes(beliefSlug)) ?? null
}
